import { useContext, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

import PortalsContext, { Portal } from './portalsContext';

interface Props {
	target: string;
	priority: number;
	children: any;
}

/**
 * Portal which can be used to render @children (React node) into @target (HTML Element ID). Ensures that only one portal with the
 * given @target is rendered, even if multiple of these components with the same @target are currently mounted anywhere in the app.
 * The decision on which portal to render is made with the following logic:
 *
 * - Take the portal with the highest @priority
 * - If multiple portals with the same @priority render the one which was mounted first.
 *
 */

const UniquePortal = (props: Props) => {
	const portal = useRef<Portal | null>(null);
	const { registerPortal, canRenderPortal, unregisterPortal } = useContext(PortalsContext);

	useEffect(() => {
		if (portal.current === null) {
			portal.current = registerPortal!(props.target, props.priority);
		}
		return () => {
			if (portal.current !== null) {
				unregisterPortal(portal.current.variant, portal.current.id || null);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const targetEl = document.getElementById(props.target);

	if (
		!portal.current ||
		!targetEl ||
		!canRenderPortal(portal.current.variant, portal.current.id || null)
	) {
		return null;
	} else {
		return ReactDOM.createPortal(props.children, targetEl);
	}
};

export default UniquePortal;
